import { fromJS, List } from 'immutable';
import * as reduxHelper from 'store/utils/reduxHelper';
import Payroll from 'models/FinancialProjections/Payroll';
import MonthlyPayroll from 'models/FinancialProjections/MonthlyPayroll';
import { byId } from 'store/utils/sortFunctions';
import types from './types';

export const initialState = fromJS({
  ...reduxHelper.applyReceiveInfo({
    data: { byId: {}, byScenario: {} },
    errorMessage: '',
    updatePending: false,
  }),
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case types.PAYROLL_FETCH:
    case types.PAYROLL_CREATE:
    case types.PAYROLL_UPDATE:
    case types.PAYROLLS_LIST:
      return state.merge(
        reduxHelper.applyRequestInfo({
          updatePending: true,
        })
      );
    case types.PAYROLLS_LIST_SUCCEEDED:
      return state.merge(
        reduxHelper.applyReceiveInfo({
          data: {
            byId: action.payload.results.reduce(
              (prevData, payroll) =>
                prevData.set(String(payroll.id), new Payroll(payroll)).sort(byId),
              state.get('data').get('byId')
            ),
            byScenario: state
              .get('data')
              .get('byScenario')
              .set(
                String(action.payload.scenario),
                fromJS(action.payload.results.map(payroll => new Payroll(payroll)).sort(byId))
              ),
          },
        })
      );
    case types.PAYROLL_FETCH_SUCCEEDED:
    case types.PAYROLL_CREATE_SUCCEEDED:
    case types.PAYROLL_UPDATE_SUCCEEDED:
      return (() => {
        const scenarioId = String(action.payload.scenario);
        const scenarioPayrolls = state
          .get('data')
          .get('byScenario')
          .get(scenarioId, new List())
          .filter(payroll => payroll.id !== action.payload.id)
          .push(new Payroll(action.payload))
          .sort(byId);
        return state.merge(
          reduxHelper.applyReceiveInfo({
            updatePending: false,
            data: {
              byId: state
                .get('data')
                .get('byId')
                .set(String(action.payload.id), new Payroll(action.payload)),
              byScenario: state.get('data').get('byScenario').set(scenarioId, scenarioPayrolls),
            },
          })
        );
      })();
    case types.PAYROLL_DELETE_SUCCEEDED:
      return state.merge(
        reduxHelper.applyReceiveInfo({
          updatePending: false,
          data: {
            byId: state.get('data').get('byId').delete(String(action.payload.id)),
            byScenario: state
              .get('data')
              .get('byScenario')
              .set(
                String(action.payload.scenario),
                state
                  .get('data')
                  .get('byScenario')
                  .get(String(action.payload.scenario), new List())
                  .filter(payroll => payroll.id !== action.payload.id)
              ),
          },
        })
      );
    case types.MONTHLY_PAYROLL_UPDATE_SUCCEEDED:
      return (() => {
        const {
          scenario: scenarioId,
          payroll: payrollId,
          ...updatedMonthlyPayroll
        } = action.payload;
        // Fetching the payroll that will be updated.
        const updatedPayroll = state.get('data').get('byId').get(String(payrollId));
        // Fetching the amount of the old monthly payroll.
        const { amount: oldMonthlyPayrollAmount } = updatedPayroll.yearly_payrolls[
          updatedMonthlyPayroll.year
        ]
          .get(String(updatedMonthlyPayroll.id))
          .toJS();
        // Recalculating the new yearly total.
        updatedPayroll.yearly_totals[updatedMonthlyPayroll.year] +=
          +updatedMonthlyPayroll.amount - +oldMonthlyPayrollAmount;
        // Resetting the new monthly payrolls.
        updatedPayroll.yearly_payrolls[updatedMonthlyPayroll.year] = updatedPayroll.yearly_payrolls[
          updatedMonthlyPayroll.year
        ].set(
          String(updatedMonthlyPayroll.id),
          new MonthlyPayroll({
            id: Number(updatedMonthlyPayroll.id),
            amount: Number(updatedMonthlyPayroll.amount),
            year: Number(updatedMonthlyPayroll.year),
            month: Number(updatedMonthlyPayroll.month),
            payroll: Number(payrollId),
          })
        );
        // Resetting the payrolls by scenario.
        const scenarioPayrolls = state
          .get('data')
          .get('byScenario')
          .get(String(scenarioId), new List())
          .filter(payroll => String(payroll.id) !== String(payrollId))
          .push(updatedPayroll)
          .sort(byId);
        // Returning the new state.
        return state.merge(
          reduxHelper.applyReceiveInfo({
            updatePending: false,
            data: {
              byId: state.get('data').get('byId').set(String(action.payload.id), updatedPayroll),
              byScenario: state
                .get('data')
                .get('byScenario')
                .set(String(scenarioId), scenarioPayrolls),
            },
          })
        );
      })();
    case types.MONTHLY_PAYROLL_UPDATE_FAILED:
    case types.PAYROLL_UPDATE_FAILED:
      return state.merge(
        reduxHelper.applyErrorInfo({
          updatePending: false,
          errorMessage: action.payload,
        })
      );
    case types.PAYROLL_FETCH_FAILED:
    case types.PAYROLLS_LIST_FAILED:
      return state.merge(
        reduxHelper.applyErrorInfo({
          errorMessage: action.payload,
        })
      );
    case types.PAYROLL_CLEAR_ERROR:
      return state.merge(reduxHelper.applyReceiveInfo());
    default:
      return state;
  }
}
